import {
  ArrowLeft,
  NotePencil,
  Settings,
  Edit,
  Library,
  MenuCollapse,
  MenuExpand,
  Sliders,
} from '@sweep-io/sweep-design/dist/icons';
import { Stack, Box } from '@mui/material';
import { ReactNode } from 'react';
import useAgentsChats from '../useAgentsChats';
import { Typography, colors, Tooltip, IconButton } from '@sweep-io/sweep-design';
import { AgentMenuItem } from '../AgentMenuItem';
import { useAgentActions } from '../useAgentActions';
import { AgentType } from '@server/ai';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';

const AgentAction = ({
  icon,
  text,
  isSelected,
  onClick,
  isDisabled,
  isCollapsed,
  showToggle,
  onToggleCollapse,
}: {
  icon: ReactNode;
  text: string;
  isSelected: boolean;
  onClick: () => void;
  isDisabled: boolean;
  isCollapsed?: boolean;
  showToggle?: boolean;
  onToggleCollapse?: () => void;
}) => {
  const renderContent = () => {
    if (!isCollapsed) {
      // Expanded layout - normal layout
      return (
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          sx={{ position: 'relative' }}
        >
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            sx={{
              justifyContent: 'flex-start',
              width: 'auto',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
            <Box
              sx={{
                opacity: 1,
                width: 'auto',
                overflow: 'hidden',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Box sx={{ whiteSpace: 'nowrap' }}>
                <Typography variant="body" color={isDisabled ? colors.grey[500] : undefined}>
                  {text}
                </Typography>
              </Box>
            </Box>
          </Stack>
          {showToggle && onToggleCollapse && (
            <Box
              sx={{
                opacity: 1,
                transform: 'scale(1) translateX(0)',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCollapse();
                }}
                variant="flat"
              >
                <MenuCollapse />
              </IconButton>
            </Box>
          )}
        </Stack>
      );
    }

    // Collapsed layout
    if (showToggle && onToggleCollapse) {
      // For "All agents" item - show only expand button, no icon
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse();
            }}
            variant="flat"
          >
            <MenuExpand />
          </IconButton>
        </Box>
      );
    }

    // For other items - show centered icon
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '24px',
            minHeight: '24px',
          }}
        >
          {icon}
        </Box>
      </Box>
    );
  };

  const content = (
    <AgentMenuItem
      isSelected={isSelected}
      isDisabled={isDisabled}
      onClick={onClick}
      sx={
        isCollapsed
          ? {
              paddingLeft: '4px',
              paddingRight: '4px',
              justifyContent: 'center',
              display: 'flex',
              alignItems: 'center',
              minHeight: '32px',
            }
          : undefined
      }
    >
      {renderContent()}
    </AgentMenuItem>
  );

  return (
    <Tooltip
      title={
        isCollapsed && !(showToggle && onToggleCollapse)
          ? text
          : isDisabled
            ? `To edit the agent's settings, please contact your admin`
            : undefined
      }
      placement={isCollapsed ? 'right' : undefined}
    >
      <span>{content}</span>
    </Tooltip>
  );
};

const AgentActions = ({
  isCollapsed,
  onToggleCollapse,
}: {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) => {
  const { activeAgent, clearActiveAgentClickHandler } = useAgentsChats();
  const { monitoringMenuConfigurations } = useFeatureToggle();
  const {
    newChatAction,
    settingsAction,
    bestPracticesAction,
    configurationsAction,
    settingsDialog,
    isSettingsOpen,
    promptsLibraryAction,
    promptsLibraryDialog,
    configurationsDialog,
  } = useAgentActions(activeAgent, {
    origin: 'sidebar',
    mode: 'expanded',
  });

  return (
    <>
      {settingsDialog}
      {promptsLibraryDialog}
      {configurationsDialog}
      <Stack gap={2}>
        <AgentAction
          icon={<ArrowLeft />}
          onClick={clearActiveAgentClickHandler}
          text="All agents"
          isDisabled={false}
          isSelected={false}
          isCollapsed={isCollapsed}
          showToggle={true}
          onToggleCollapse={onToggleCollapse}
        />
        <Stack gap={1}>
          {newChatAction && (
            <AgentAction
              isSelected={false}
              icon={<NotePencil />}
              text="New chat"
              onClick={newChatAction.onClick}
              isDisabled={newChatAction.disabled || false}
              isCollapsed={isCollapsed}
            />
          )}
          {promptsLibraryAction && activeAgent?.type === AgentType.Documentation && (
            <AgentAction
              isSelected={false}
              icon={<Library />}
              text="Prompts library"
              onClick={promptsLibraryAction.onClick}
              isDisabled={promptsLibraryAction.disabled || false}
              isCollapsed={isCollapsed}
            />
          )}
          {bestPracticesAction && (
            <AgentAction
              isSelected={false}
              icon={<Edit color={bestPracticesAction.disabled ? colors.grey[500] : undefined} />}
              text="Best Practices"
              onClick={bestPracticesAction.onClick}
              isDisabled={bestPracticesAction.disabled || false}
              isCollapsed={isCollapsed}
            />
          )}
          {monitoringMenuConfigurations && configurationsAction && (
            <AgentAction
              isSelected={false}
              icon={
                <Sliders color={configurationsAction.disabled ? colors.grey[500] : undefined} />
              }
              text="Configurations"
              onClick={configurationsAction.onClick}
              isDisabled={configurationsAction.disabled || false}
              isCollapsed={isCollapsed}
            />
          )}
          {settingsAction && (
            <AgentAction
              isSelected={isSettingsOpen}
              icon={<Settings color={settingsAction.disabled ? colors.grey[500] : undefined} />}
              text="Agent settings"
              onClick={settingsAction.onClick}
              isDisabled={settingsAction.disabled || false}
              isCollapsed={isCollapsed}
            />
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default AgentActions;
